<script setup lang="ts">
import type { ContentFile } from '~/models/Content/ContentFile'
import type { BaseItem } from '~/models/Content/BaseItem'
import { computed } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFile } from '@fortawesome/duotone-regular-svg-icons'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useFileInfo from '~/composables/useFileInfo'
import { useContentHelper } from '~/composables/useContentHelper'
import { useAppColor } from '~/composables/useAppColor'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'

const { children } = defineProps<{
  children: BaseItem[]
  title?: string
}>()

const { themeColor } = useAppColor()
const { isFile } = useContentHelper()
const { readableMimeType, readableFileSize } = useFileInfo()
const { buildFileUrl } = useUrlBuilder()

const files = computed(() => children.filter(child => isFile(child)))

const getFileInfo = (item: ContentFile) => {
  const mimeType = item.file?.mimeType ? readableMimeType(item.file.mimeType) : ''
  const fileSize = item.file?.fileSize ? readableFileSize(item.file.fileSize) : ''
  return [mimeType, fileSize].filter(Boolean).join(' ')
}
</script>

<template>
  <div
    v-if="files.length"
    class="grid gap-4 border-4 border-(--theme-5) p-4 px-8 pt-8 pb-14 outline-6 outline-(--theme-20)"
  >
    <div class="flex items-center gap-1">
      <FontAwesomeIcon :icon="faFile" size="3x" />
      <h2 class="text-xl font-bold">
        {{ title ?? $t('filesForDownload') }}
      </h2>
    </div>
    <ul
      class="grid auto-rows-fr border-(--card-border) md:grid-cols-2"
      :class="{ 'border-t-1': children.length > 1 }"
    >
      <li
        v-for="(item, i) in files"
        :key="item.locationId"
        class="animate-fade flex items-center justify-between gap-4 border-b-1 border-(--card-border) py-3 pr-5 pl-1 text-lg font-medium transition duration-100"
        :class="{ 'md:border-r-1': i % 2 === 0 && files.length > 1 }"
      >
        <a
          class="truncate rounded-xl px-3 py-2 text-lg hover:underline focus-visible:ring"
          :href="buildFileUrl(item)"
        >

          {{ item.title }}

        </a>
        <div class="flex shrink-0 items-center gap-2">
          <span class="text-xs">
            {{ getFileInfo(item as ContentFile) }}
          </span>
          <CardMenu
            :resource="item"
            :toggle-color="themeColor"
            toggle-shade="light"
            toggle-variant="solid"
          />
        </div>
      </li>
    </ul>
  </div>
</template>
